import React from 'react';

const CreateExamForm = ({ examForm, handleExamFormChange, handleCreateExam, handleSectionsChange, handleSectionChange, setExamForm, loading, onCancel, currentStep, setCurrentStep }) => {
  
  const handleNext = () => {
    // Validate current step before moving to next
    if (currentStep === 1) {
      if (!examForm.name || !examForm.startDate || !examForm.startTime || !examForm.endDate || !examForm.endTime) {
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate based on selection mode
      if (examForm.useRandomSelection) {
        // Random selection mode validation
        if (!examForm.totalQuestions) {
          alert('Please enter total questions');
          return;
        }
        if (!examForm.selectedSubjects || examForm.selectedSubjects.length === 0) {
          alert('Please select at least one subject');
          return;
        }
        if (parseInt(examForm.totalQuestions) < examForm.selectedSubjects.length) {
          alert(`Total questions must be at least ${examForm.selectedSubjects.length} (one per subject)`);
          return;
        }
      } else {
        // Manual mode validation - questions must match total
        const totalSectionQuestions = examForm.sections.reduce((sum, sec) => sum + parseInt(sec.questions || 0), 0);
        if (!examForm.totalQuestions || totalSectionQuestions !== parseInt(examForm.totalQuestions)) {
          alert(`Section questions (${totalSectionQuestions}) must equal total questions (${examForm.totalQuestions})`);
          return;
        }
        
        // Check if any section has duration filled
        const hasSectionDurations = examForm.sections.some(sec => sec.duration && parseFloat(sec.duration) > 0);
        
        // If per-question timer is enabled, validate timer logic
        if (examForm.hasPerQuestionTimer && examForm.timePerQuestion) {
          if (!hasSectionDurations) {
            alert('Per-question timer is enabled. Please set duration for all sections or disable the per-question timer.');
            return;
          }
          
          const timePerQinMinutes = parseFloat(examForm.timePerQuestion) / 60;
          
          // Check each section's duration matches questions × time per question
          for (let i = 0; i < examForm.sections.length; i++) {
            const section = examForm.sections[i];
            if (!section.duration || parseFloat(section.duration) <= 0) {
              alert(`Section ${i + 1} is missing duration. All sections must have durations when per-question timer is enabled.`);
              return;
            }
            
            const expectedDuration = (parseFloat(section.questions || 0) * timePerQinMinutes).toFixed(2);
            const actualDuration = parseFloat(section.duration || 0).toFixed(2);
            
            if (Math.abs(expectedDuration - actualDuration) > 0.1) {
              alert(
                `Section ${i + 1} duration mismatch!\n\n` +
                `Expected: ${section.questions} questions × ${examForm.timePerQuestion}s = ${expectedDuration} minutes\n` +
                `Actual: ${actualDuration} minutes\n\n` +
                `Please adjust the section duration to match.`
              );
              return;
            }
          }
        }
        
        // Calculate and set total duration from sections (if provided)
        if (hasSectionDurations) {
          const totalDuration = examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0);
          setExamForm({
            ...examForm,
            duration: totalDuration.toFixed(1)
          });
        }
      }
      
      setCurrentStep(3);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleSubmitStep = (e) => {
    e.preventDefault();
    if (currentStep === 3) {
      handleCreateExam(e);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-indigo-100">
      {/* Step Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-2xl font-bold text-gray-900">Create New Exam</h4>
          <span className="text-sm text-gray-500">Step {currentStep} of 3</span>
        </div>
        
        {/* Progress Bar */}
        <div className="flex items-center gap-2">
          <div className={`flex-1 h-2 rounded-full transition ${currentStep >= 1 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
          <div className={`flex-1 h-2 rounded-full transition ${currentStep >= 2 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
          <div className={`flex-1 h-2 rounded-full transition ${currentStep >= 3 ? 'bg-indigo-600' : 'bg-gray-200'}`}></div>
        </div>
        
        {/* Step Labels */}
        <div className="flex items-center justify-between mt-2 text-xs font-semibold">
          <span className={currentStep === 1 ? 'text-indigo-600' : 'text-gray-400'}>Basic Details</span>
          <span className={currentStep === 2 ? 'text-indigo-600' : 'text-gray-400'}>Sections</span>
          <span className={currentStep === 3 ? 'text-indigo-600' : 'text-gray-400'}>Marking & Timing</span>
        </div>
      </div>

      <form onSubmit={handleSubmitStep} className="space-y-6">
        {/* STEP 1: Basic Details */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h5 className="font-bold text-indigo-900 mb-2">📝 Step 1: Basic Information</h5>
              <p className="text-sm text-indigo-700">Enter the exam name, schedule, and visibility settings.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Exam Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Exam Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={examForm.name}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., Mid-term Mathematics Exam"
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  required
                  value={examForm.startDate}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              {/* Start Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Time *
                </label>
                <input
                  type="time"
                  name="startTime"
                  required
                  value={examForm.startTime}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  name="endDate"
                  required
                  value={examForm.endDate}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              {/* End Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Time *
                </label>
                <input
                  type="time"
                  name="endTime"
                  required
                  value={examForm.endTime}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                />
              </div>

              {/* Banner Image URL */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Banner Image URL (Optional)
                </label>
                <input
                  type="url"
                  name="bannerImage"
                  value={examForm.bannerImage}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="https://example.com/banner.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Provide a URL to an image for the exam banner</p>
              </div>

              {/* Exam Visibility */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Exam Visibility *
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-indigo-50 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50">
                    <input
                      type="radio"
                      name="visibility"
                      value="organization"
                      checked={examForm.visibility === 'organization'}
                      onChange={handleExamFormChange}
                      className="mt-1 w-5 h-5 text-indigo-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">Organization Only</span>
                      <p className="text-sm text-gray-600">Only students from your organization can see and take this exam</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-green-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                    <input
                      type="radio"
                      name="visibility"
                      value="public"
                      checked={examForm.visibility === 'public'}
                      onChange={handleExamFormChange}
                      className="mt-1 w-5 h-5 text-green-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">Public</span>
                      <p className="text-sm text-gray-600">All students (from any organization) can see and take this exam</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Sections */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h5 className="font-bold text-green-900 mb-2">📚 Step 2: Exam Sections</h5>
              <p className="text-sm text-green-700">Choose how questions will be selected for this exam.</p>
            </div>

            <div className="grid gap-6">
              {/* Question Selection Mode */}
              <div className="space-y-3 mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Question Selection Mode *
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-indigo-50 has-[:checked]:border-indigo-500 has-[:checked]:bg-indigo-50">
                    <input
                      type="radio"
                      name="useRandomSelection"
                      value="false"
                      checked={!examForm.useRandomSelection}
                      onChange={() => setExamForm({ ...examForm, useRandomSelection: false })}
                      className="mt-1 w-5 h-5 text-indigo-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">Manual - Add Questions Later</span>
                      <p className="text-sm text-gray-600">Define sections and manually add exact questions after exam creation</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-green-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                    <input
                      type="radio"
                      name="useRandomSelection"
                      value="true"
                      checked={examForm.useRandomSelection === true}
                      onChange={() => setExamForm({ ...examForm, useRandomSelection: true, numberOfSections: 1 })}
                      className="mt-1 w-5 h-5 text-green-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">🎲 Random Selection from Subjects</span>
                      <p className="text-sm text-gray-600">Select subjects and system will randomly pick questions for each student</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Total Questions First */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Questions *
                </label>
                <input
                  type="number"
                  name="totalQuestions"
                  required
                  min="1"
                  value={examForm.totalQuestions}
                  onChange={(e) => {
                    const value = e.target.value;
                    setExamForm({
                      ...examForm,
                      totalQuestions: value,
                      // Auto-update single section questions
                      sections: examForm.numberOfSections === 1 
                        ? [{ name: examForm.name || 'Section 1', questions: value, duration: examForm.sections[0]?.duration || '' }]
                        : examForm.sections
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., 50"
                />
              </div>

              {/* Random Selection Mode: Subject Selection */}
              {examForm.useRandomSelection === true && (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h6 className="font-semibold text-blue-900 mb-2">🎲 Random Question Selection</h6>
                    <p className="text-sm text-blue-700">Select subjects from which questions will be randomly picked for each student.</p>
                  </div>

                  {/* Subject Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Select Subjects/Chapters *
                    </label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {['Physics', 'Chemistry', 'Mathematics', 'Biology', 'English', 'Computer Science', 'History', 'Geography'].map((subject) => (
                        <label key={subject} className="flex items-center gap-3 cursor-pointer p-3 border-2 rounded-lg transition hover:bg-gray-50 has-[:checked]:border-green-500 has-[:checked]:bg-green-50">
                          <input
                            type="checkbox"
                            checked={examForm.selectedSubjects?.includes(subject) || false}
                            onChange={(e) => {
                              const selected = examForm.selectedSubjects || [];
                              if (e.target.checked) {
                                setExamForm({ ...examForm, selectedSubjects: [...selected, subject] });
                              } else {
                                setExamForm({ ...examForm, selectedSubjects: selected.filter(s => s !== subject) });
                              }
                            }}
                            className="w-5 h-5 text-green-600 border-gray-300 rounded"
                          />
                          <span className="font-semibold text-gray-900">{subject}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Select one or more subjects. Questions will be distributed equally across selected subjects.
                    </p>
                  </div>

                  {/* Distribution Summary */}
                  {examForm.selectedSubjects && examForm.selectedSubjects.length > 0 && examForm.totalQuestions && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h6 className="font-semibold text-green-900 mb-2">Question Distribution</h6>
                      <div className="space-y-2 text-sm">
                        <p className="text-green-700">
                          <span className="font-bold">Total Questions:</span> {examForm.totalQuestions}
                        </p>
                        <p className="text-green-700">
                          <span className="font-bold">Selected Subjects:</span> {examForm.selectedSubjects.length}
                        </p>
                        <p className="text-green-700">
                          <span className="font-bold">Questions per Subject:</span> ~{Math.floor(examForm.totalQuestions / examForm.selectedSubjects.length)}
                        </p>
                        <div className="mt-3 pt-3 border-t border-green-300">
                          <p className="font-semibold text-green-900 mb-1">Distribution:</p>
                          {examForm.selectedSubjects.map((subject, idx) => (
                            <p key={subject} className="text-green-700">
                              • {subject}: {Math.floor(examForm.totalQuestions / examForm.selectedSubjects.length)} questions
                              {idx === 0 && examForm.totalQuestions % examForm.selectedSubjects.length > 0 && ` +${examForm.totalQuestions % examForm.selectedSubjects.length}`}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Mode: Number of Sections and Configuration */}
              {!examForm.useRandomSelection && (
                <>
                  {/* Number of Sections */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Sections *
                    </label>
                    <input
                      type="number"
                      name="numberOfSections"
                      required
                      min="1"
                      max="10"
                      value={examForm.numberOfSections}
                      onChange={(e) => handleSectionsChange(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      placeholder="e.g., 1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave as 1 if you don't want to divide the exam into sections
                    </p>
                  </div>

                  {/* Sections Configuration */}
                  {examForm.numberOfSections > 0 && examForm.sections && (
                <div className="space-y-4">
                  <h6 className="font-semibold text-gray-900">Configure Sections</h6>
                  {examForm.sections.map((section, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Section {index + 1} Name
                          </label>
                          <input
                            type="text"
                            value={section.name}
                            onChange={(e) => handleSectionChange(index, 'name', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder={`Section ${index + 1}`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Number of Questions *
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={section.questions}
                            onChange={(e) => handleSectionChange(index, 'questions', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Section Duration (Minutes) {examForm.hasPerQuestionTimer ? '*' : '(Optional)'}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={section.duration}
                            onChange={(e) => handleSectionChange(index, 'duration', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder={examForm.hasPerQuestionTimer ? "Required with timer" : "Optional"}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Section Questions Summary */}
                  <div className="space-y-4">
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-indigo-900">Total Questions Allocated:</span>
                        <span className={`font-bold text-lg ${
                          examForm.sections.reduce((sum, s) => sum + parseInt(s.questions || 0), 0) === parseInt(examForm.totalQuestions || 0)
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {examForm.sections.reduce((sum, s) => sum + parseInt(s.questions || 0), 0)} / {examForm.totalQuestions || 0}
                        </span>
                      </div>
                      {examForm.sections.reduce((sum, s) => sum + parseInt(s.questions || 0), 0) !== parseInt(examForm.totalQuestions || 0) && (
                        <p className="text-sm text-red-600 mt-2">
                          ⚠ Section questions must equal total questions
                        </p>
                      )}
                    </div>
                    
                    {/* Section Duration Summary */}
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-purple-900">Total Section Duration:</span>
                        <span className="font-bold text-lg text-purple-700">
                          {examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0).toFixed(1)} minutes
                        </span>
                      </div>
                      {examForm.hasPerQuestionTimer && examForm.timePerQuestion && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-purple-900">Per-Question Timer Validation:</span>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              (() => {
                                const expected = (parseInt(examForm.totalQuestions || 0) * parseFloat(examForm.timePerQuestion || 0) / 60);
                                const actual = examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0);
                                return Math.abs(expected - actual) <= 0.1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
                              })()
                            }`}>
                              {(() => {
                                const expected = (parseInt(examForm.totalQuestions || 0) * parseFloat(examForm.timePerQuestion || 0) / 60);
                                const actual = examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0);
                                return Math.abs(expected - actual) <= 0.1 ? '✓ MATCH' : '✗ MISMATCH';
                              })()}
                            </span>
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between text-purple-700">
                              <span>Expected: {examForm.totalQuestions} Q × {examForm.timePerQuestion}s</span>
                              <span className="font-semibold">
                                {(parseInt(examForm.totalQuestions || 0) * parseFloat(examForm.timePerQuestion || 0) / 60).toFixed(2)} min
                              </span>
                            </div>
                            <div className="flex justify-between text-purple-700">
                              <span>Actual (Sum of sections):</span>
                              <span className="font-semibold">
                                {examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0).toFixed(2)} min
                              </span>
                            </div>
                          </div>
                          {(() => {
                            const expected = (parseInt(examForm.totalQuestions || 0) * parseFloat(examForm.timePerQuestion || 0) / 60);
                            const actual = examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0);
                            if (Math.abs(expected - actual) > 0.1) {
                              return (
                                <p className="text-xs text-red-600 mt-2 font-semibold">
                                  ⚠ Section durations must match: {examForm.totalQuestions} questions × {examForm.timePerQuestion}s = {expected.toFixed(2)} minutes
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
          </div>
        )}

        {/* STEP 3: Marking & Timing */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-bold text-purple-900 mb-2">🎯 Step 3: Marking & Timing Details</h5>
              <p className="text-sm text-purple-700">Configure marks, duration, attempts, and negative marking.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Total Questions (Read-only, already set in Step 2) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Questions
                </label>
                <input
                  type="number"
                  name="totalQuestions"
                  required
                  min="1"
                  value={examForm.totalQuestions}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  placeholder="e.g., 50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ✓ Set in Step 2 (Sections)
                </p>
              </div>

              {/* Total Marks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Marks *
                </label>
                <input
                  type="number"
                  name="totalMarks"
                  required
                  min="1"
                  value={examForm.totalMarks}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="e.g., 100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {examForm.totalQuestions && examForm.marksPerQuestion 
                    ? `Suggested: ${examForm.totalQuestions} × ${examForm.marksPerQuestion} = ${examForm.totalQuestions * examForm.marksPerQuestion}`
                    : 'Enter total marks manually'}
                </p>
              </div>

              {/* Marks Per Question */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Marks Per Question (Optional)
                </label>
                <input
                  type="number"
                  name="marksPerQuestion"
                  min="0.5"
                  step="0.5"
                  value={examForm.marksPerQuestion}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder="Leave blank for mixed marks"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Fill this only if ALL questions have the same marks
                </p>
              </div>

              {/* Per Question Timer Toggle */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="hasPerQuestionTimer"
                    checked={examForm.hasPerQuestionTimer}
                    onChange={handleExamFormChange}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Enable Per-Question Timer</span>
                    <p className="text-xs text-gray-500">Set a specific time limit for each question</p>
                  </div>
                </label>
              </div>

              {/* Time Per Question */}
              {examForm.hasPerQuestionTimer && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Time Per Question (Seconds) *
                  </label>
                  <input
                    type="number"
                    name="timePerQuestion"
                    required
                    min="5"
                    step="5"
                    value={examForm.timePerQuestion}
                    onChange={handleExamFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="e.g., 30, 60, 90"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Each question will have this time limit. Total: {examForm.totalQuestions && examForm.timePerQuestion ? `${(parseInt(examForm.totalQuestions) * parseFloat(examForm.timePerQuestion) / 60).toFixed(1)} minutes` : '0 minutes'}
                  </p>
                </div>
              )}

              {/* Exam Duration */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Exam Duration (Minutes) *
                </label>
                {(() => {
                  const hasSectionDurations = examForm.sections.some(s => s.duration && parseFloat(s.duration) > 0);
                  const calculatedDuration = examForm.sections.reduce((sum, s) => sum + parseFloat(s.duration || 0), 0).toFixed(1);
                  
                  if (hasSectionDurations) {
                    return (
                      <>
                        <input
                          type="number"
                          name="duration"
                          required
                          min="1"
                          value={calculatedDuration}
                          readOnly
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                          placeholder="Calculated from sections"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          ✓ Automatically calculated from section durations ({calculatedDuration} minutes)
                        </p>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <input
                          type="number"
                          name="duration"
                          required
                          min="1"
                          value={examForm.duration}
                          onChange={handleExamFormChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                          placeholder="e.g., 60 for 1 hour"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter total time allowed to complete the exam
                        </p>
                      </>
                    );
                  }
                })()}
              </div>

              {/* Attempt Limit */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Attempt Limit *
                </label>
                <select
                  name="attemptLimit"
                  required
                  value={examForm.attemptLimit}
                  onChange={handleExamFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                >
                  <option value="once">Only Once</option>
                  <option value="2">2 Attempts</option>
                  <option value="3">3 Attempts</option>
                  <option value="custom">Custom Number</option>
                  <option value="unlimited">Unlimited Attempts</option>
                </select>
              </div>

              {/* Custom Attempts */}
              {examForm.attemptLimit === 'custom' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Attempts *
                  </label>
                  <input
                    type="number"
                    name="customAttempts"
                    required
                    min="1"
                    max="100"
                    value={examForm.customAttempts}
                    onChange={handleExamFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="Enter number of attempts (1-100)"
                  />
                </div>
              )}

              {/* Negative Marking */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="hasNegativeMarking"
                    checked={examForm.hasNegativeMarking}
                    onChange={handleExamFormChange}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Enable Negative Marking</span>
                    <p className="text-xs text-gray-500">Deduct marks for incorrect answers</p>
                  </div>
                </label>
              </div>

              {/* Negative Marks Per Question */}
              {examForm.hasNegativeMarking && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Negative Marks Per Wrong Answer *
                  </label>
                  <input
                    type="number"
                    name="negativeMarks"
                    required
                    min="0.1"
                    step="0.1"
                    value={examForm.negativeMarks}
                    onChange={handleExamFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="e.g., 0.25, 0.5, 1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Marks to be deducted for each incorrect answer (e.g., -0.25, -0.5, -1)
                  </p>
                </div>
              )}

              {/* Tab Shift Behavior */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Tab Shift Behavior *
                </label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-blue-50 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                    <input
                      type="radio"
                      name="tabShiftMode"
                      value="count-only"
                      checked={examForm.tabShiftMode === 'count-only' || !examForm.tabShiftMode}
                      onChange={handleExamFormChange}
                      className="mt-1 w-5 h-5 text-blue-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">Count Only</span>
                      <p className="text-sm text-gray-600">Track tab switches but allow exam to continue (count will be shown in results)</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer p-4 border-2 rounded-lg transition hover:bg-red-50 has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                    <input
                      type="radio"
                      name="tabShiftMode"
                      value="auto-submit"
                      checked={examForm.tabShiftMode === 'auto-submit'}
                      onChange={handleExamFormChange}
                      className="mt-1 w-5 h-5 text-red-600"
                    />
                    <div>
                      <span className="font-semibold text-gray-900">Auto-Submit After 5 Switches</span>
                      <p className="text-sm text-gray-600">Automatically submit exam after 5 tab switches for security (count will be shown in results)</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Show Result Immediately */}
              <div className="md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="showResultImmediately"
                    checked={examForm.showResultImmediately}
                    onChange={handleExamFormChange}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Show Results Immediately</span>
                    <p className="text-xs text-gray-500">Students will see their results right after completing the exam</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            Cancel
          </button>

          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="px-6 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              ← Previous
            </button>
          )}

          <div className="flex-1"></div>

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={
                (currentStep === 1 && (!examForm.name || !examForm.startDate || !examForm.startTime || !examForm.endDate || !examForm.endTime)) ||
                (currentStep === 2 && (!examForm.totalQuestions || examForm.totalQuestions < 1))
              }
              className={`px-8 py-3 rounded-lg font-semibold transition ${
                ((currentStep === 1 && examForm.name && examForm.startDate && examForm.startTime && examForm.endDate && examForm.endTime) ||
                 (currentStep === 2 && examForm.totalQuestions && examForm.totalQuestions >= 1))
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {currentStep === 1 ? 'Next: Sections →' : 'Next: Marking & Timing →'}
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-3 rounded-lg font-semibold transition ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-lg'
              }`}
            >
              {loading ? 'Creating...' : '✓ Create Exam & Add Questions'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateExamForm;
